import * as React from 'react';
import MuiIconButton, { type IconButtonProps as MuiIconButtonProps } from '@mui/material/IconButton';
import Icon from '@mui/material/Icon';

export interface IconButtonProps extends Omit<MuiIconButtonProps, 'color'> {
  /** react-toolbox: icon name (material icon string) */
  icon?: string;
  /** react-toolbox: accent color */
  accent?: boolean;
  /** react-toolbox: primary color */
  primary?: boolean;
  /** react-toolbox: neutral/default styling */
  neutral?: boolean;
  /** react-toolbox: ripple effect (inverted - false means disable) */
  ripple?: boolean;
  /** MUI color override */
  color?: MuiIconButtonProps['color'];
}

/**
 * IconButton wrapper - maps react-toolbox IconButton to MUI IconButton
 * - `icon` prop renders a material icon
 * - `accent` maps to color="secondary"
 * - `primary` maps to color="primary"
 * - `ripple={false}` maps to disableRipple
 */
const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ icon, accent, primary, neutral, ripple = true, children, color: colorProp, ...rest }, ref) => {
    // Determine color
    let color: MuiIconButtonProps['color'] = colorProp;
    if (!color) {
      if (accent) {
        color = 'secondary';
      } else if (primary) {
        color = 'primary';
      } else if (neutral) {
        color = 'default';
      }
    }

    // Handle ripple
    const disableRipple = ripple === false;

    // Render icon content
    const iconContent = icon ? <Icon>{icon}</Icon> : children;

    return (
      <MuiIconButton ref={ref} color={color} disableRipple={disableRipple} {...rest}>
        {iconContent}
      </MuiIconButton>
    );
  },
);

IconButton.displayName = 'IconButton';

export default IconButton;
